import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import api from '../api';
import { useNavigate } from 'react-router-dom';

const SignUpPage = ({ onClose, onSwitchToLogin, isOverlay = false }) => {
  const [step, setStep] = useState(1); // 1: Form, 2: OTP Verification, 3: Success
  const [role, setRole] = useState('creator');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    websiteUrl: ''
  });
  const [otp, setOtp] = useState('');
  const [captcha, setCaptcha] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const shellClassName = isOverlay
    ? "w-full"
    : "site-shell min-h-screen flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8";
  const cardClassName = isOverlay
    ? "auth-card !bg-transparent !border-0 !shadow-none rounded-[1.5rem] p-4 sm:p-5"
    : "auth-card rounded-[2rem] p-6 sm:p-8 lg:p-10";
  const successShellClassName = isOverlay
    ? "w-full"
    : "site-shell min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8";
  const successCardClassName = isOverlay
    ? "auth-card !bg-transparent !border-0 !shadow-none max-w-lg w-full rounded-[1.5rem] p-4 sm:p-5 text-center"
    : "auth-card max-w-lg w-full rounded-[2rem] p-8 sm:p-10 text-center";

  // Generate CAPTCHA
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10);
    const num2 = Math.floor(Math.random() * 10);
    const operations = ['+', '-', '*'];
    const operation = operations[Math.floor(Math.random() * operations.length)];
    
    let answer;
    switch (operation) {
      case '+':
        answer = num1 + num2;
        break;
      case '-':
        answer = num1 - num2;
        break;
      case '*':
        answer = num1 * num2;
        break;
      default:
        answer = num1 + num2;
    }
    
    const captchaText = `${num1} ${operation} ${num2} = ?`;
    setCaptchaAnswer(answer.toString());
    
    // Draw CAPTCHA on canvas
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Background
      ctx.fillStyle = '#f0f0f0';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Add noise lines
      for (let i = 0; i < 5; i++) {
        ctx.strokeStyle = `hsl(${Math.random() * 360}, 50%, 50%)`;
        ctx.beginPath();
        ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
        ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
        ctx.stroke();
      }
      
      // Text
      ctx.font = '24px Arial';
      ctx.fillStyle = '#333';
      ctx.textAlign = 'center';
      ctx.fillText(captchaText, canvas.width / 2, canvas.height / 2 + 8);
      
      // Add noise dots
      for (let i = 0; i < 20; i++) {
        ctx.fillStyle = `hsl(${Math.random() * 360}, 50%, 50%)`;
        ctx.fillRect(Math.random() * canvas.width, Math.random() * canvas.height, 2, 2);
      }
    }
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  // OTP Timer
  useEffect(() => {
    let interval = null;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer(otpTimer - 1);
      }, 1000);
    } else if (otpTimer === 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const { name, email, password, confirmPassword, companyName, websiteUrl } = formData;
    
    if (!name || !email || !password || !confirmPassword) {
      setError('Please fill all required fields.');
      return false;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return false;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return false;
    }
    
    if (role === 'brand' && (!companyName || !websiteUrl)) {
      setError('Please provide company name and website URL for brands.');
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      return false;
    }
    
    if (captcha !== captchaAnswer) {
      setError('CAPTCHA answer is incorrect.');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      // Send OTP to email
      await api.post('/auth/send-otp', { 
        email: formData.email,
        name: formData.name 
      });
      
      setStep(2);
      setOtpTimer(300); // 5 minutes
      setSuccess('OTP sent to your email. Please check your inbox.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerification = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP.');
      setLoading(false);
      return;
    }

    try {
      // Verify OTP and create account
      const payload = {
        ...formData,
        role,
        otp
      };
      
      await api.post('/auth/verify-signup', payload);
      setStep(3);
      setSuccess('Account created successfully! Please log in to continue.');
      // Don't redirect automatically - let user read the message and click login button
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    if (otpTimer > 0) return;
    
    setError('');
    setLoading(true);
    
    try {
      await api.post('/auth/send-otp', { 
        email: formData.email,
        name: formData.name 
      });
      
      setOtpTimer(300);
      setSuccess('OTP resent successfully.');
    } catch (err) {
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 3) {
    return (
      <>
        <Helmet>
          <title>Registration Complete - CollabOrb</title>
          <meta name="description" content="Account successfully created on CollabOrb" />
        </Helmet>
        <div className={successShellClassName}>
          <div className={successCardClassName}>
            <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-2xl bg-collab-accent-soft">
              <svg className="h-7 w-7 text-collab-brand-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="section-kicker mt-6 mb-4 !bg-[rgba(83,158,203,0.14)] !text-collab-brand-accent">Account created</div>
            <h2 className="text-3xl font-extrabold text-collab-inner">
              Registration complete
            </h2>
            <p className="mt-3 text-sm text-slate-600">
              Your account is ready. Log in to start exploring campaigns, creators, and your new workspace.
            </p>
            {success && <p className="mt-3 text-collab-brand-accent">{success}</p>}

            <div className="mt-8">
              <button
                onClick={() => {
                  if (onSwitchToLogin) {
                    onSwitchToLogin();
                  } else {
                    navigate('/');
                  }
                }}
                className="primary-button !w-full !justify-center"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{step === 1 ? 'Sign Up' : 'Verify Email'} - CollabOrb</title>
        <meta name="description" content="Create your CollabOrb account and join the creator marketing platform" />
      </Helmet>
      
      <div className={shellClassName}>
        <div className={isOverlay ? "w-full" : "max-w-2xl w-full"}>
          <div className={cardClassName}>
            <div>
              <div className={`section-kicker ${isOverlay ? 'mb-3' : 'mb-4'}`}>{step === 1 ? 'Account setup' : 'Email verification'}</div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-collab-inner">
                {step === 1 ? 'Create your account' : 'Verify your email'}
              </h2>
              <p className="mt-2 text-sm text-slate-500">
                {step === 1
                  ? 'Join CollabOrb as a creator or brand with a guided onboarding flow.'
                  : `Enter the 6-digit code sent to ${formData.email}`}
              </p>
            </div>

            <div className={`flex items-center space-x-4 ${isOverlay ? 'mt-4' : 'mt-6'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                step >= 1 ? 'bg-collaborb-inner text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                1
              </div>
              <div className={`h-1 flex-1 rounded-full ${step >= 2 ? 'bg-collaborb-inner' : 'bg-gray-200'}`}></div>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                step >= 2 ? 'bg-collaborb-inner text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                2
              </div>
            </div>

          {step === 1 ? (
            <form className={`${isOverlay ? 'mt-5' : 'mt-6'} space-y-4`} onSubmit={handleSubmit}>
              <div className="space-y-3">
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-slate-700">Account Type</label>
                  <select
                    id="role"
                    value={role}
                    onChange={e => setRole(e.target.value)}
                    className="input-surface mt-1"
                  >
                    <option value="creator">Creator</option>
                    <option value="brand">Brand</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700">Full Name</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="input-surface mt-1"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700">Email Address</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="input-surface mt-1"
                    placeholder="Enter your email"
                  />
                </div>

                {role === 'brand' && (
                  <>
                    <div>
                      <label htmlFor="companyName" className="block text-sm font-medium text-slate-700">Company Name</label>
                      <input
                        id="companyName"
                        name="companyName"
                        type="text"
                        required={role === 'brand'}
                        value={formData.companyName}
                        onChange={handleInputChange}
                        className="input-surface mt-1"
                        placeholder="Enter company name"
                      />
                    </div>
                    <div>
                      <label htmlFor="websiteUrl" className="block text-sm font-medium text-slate-700">Website URL</label>
                      <input
                        id="websiteUrl"
                        name="websiteUrl"
                        type="url"
                        required={role === 'brand'}
                        value={formData.websiteUrl}
                        onChange={handleInputChange}
                        className="input-surface mt-1"
                        placeholder="https://your-website.com"
                      />
                    </div>
                  </>
                )}

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-slate-700">Password</label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    className="input-surface mt-1"
                    placeholder="Enter password (min 6 characters)"
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700">Confirm Password</label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="input-surface mt-1"
                    placeholder="Confirm your password"
                  />
                </div>

                <div>
                  <label htmlFor="captcha-input" className="block text-sm font-medium text-slate-700 mb-2">Security Verification</label>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <canvas
                      ref={canvasRef}
                      width="150"
                      height="50"
                      className="rounded-2xl border border-slate-200 bg-slate-50"
                      aria-label="CAPTCHA challenge"
                    ></canvas>
                    <button
                      type="button"
                      onClick={generateCaptcha}
                      className="secondary-button !px-4 !py-2 !text-sm"
                    >
                      Refresh
                    </button>
                  </div>
                  <input
                    id="captcha-input"
                    type="text"
                    value={captcha}
                    onChange={(e) => setCaptcha(e.target.value)}
                    placeholder="Enter the answer"
                    className="input-surface mt-2"
                    required
                  />
                </div>
              </div>

              {error && (
                <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded-2xl">
                  {error}
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="primary-button !w-full !justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending OTP...' : 'Send Verification Code'}
                </button>
              </div>

              <div className="text-center">
                <span className="text-sm text-slate-600">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      if (onSwitchToLogin) {
                        onSwitchToLogin();
                      } else {
                        navigate('/');
                      }
                    }}
                    className="font-medium text-collaborb-band4 hover:text-collaborb-spherical-mid"
                  >
                    Sign in
                  </button>
                </span>
              </div>
            </form>
          ) : (
            <form className={`${isOverlay ? 'mt-5' : 'mt-6'} space-y-4`} onSubmit={handleOtpVerification}>
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-slate-700">
                  Verification Code
                </label>
                <input
                  id="otp"
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="input-surface mt-1 text-center text-2xl tracking-widest"
                  placeholder="000000"
                  maxLength="6"
                  required
                />
                <p className="mt-2 text-sm text-slate-600">
                  {otpTimer > 0 ? (
                    <>Code expires in {Math.floor(otpTimer / 60)}:{(otpTimer % 60).toString().padStart(2, '0')}</>
                  ) : (
                    <>Code expired. Please request a new one.</>
                  )}
                </p>
              </div>

              {error && (
                <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded-2xl">
                  {error}
                </div>
              )}

              {success && (
                <div className="text-green-600 text-sm text-center bg-green-50 p-3 rounded-2xl">
                  {success}
                </div>
              )}

              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="primary-button !w-full !justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Verifying...' : 'Verify & Create Account'}
                </button>

                <button
                  type="button"
                  onClick={resendOtp}
                  disabled={otpTimer > 0 || loading}
                  className="secondary-button !w-full !justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {otpTimer > 0 ? `Resend in ${otpTimer}s` : 'Resend Code'}
                </button>

                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="w-full flex justify-center py-2 px-4 text-sm font-medium text-collaborb-band4 hover:text-collaborb-spherical-mid"
                >
                  Back to Form
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default SignUpPage;
